import React, { useCallback, useMemo, useState } from 'react';

const createFieldRefs = fields =>
  Object.fromEntries(Object.keys(fields).map(fieldName => [fieldName, React.createRef()]));

export const useYupValidation = validationRules => {
  const [errors, setErrors] = useState({});
  const fieldRefs = useMemo(() => createFieldRefs(validationRules.fields), [validationRules.fields]);

  const scrollAndFocusErrorField = useCallback(
    errorField => {
      const errorFieldRef = fieldRefs[errorField];
      const targetNode = errorFieldRef?.current?.node ?? errorFieldRef?.current;

      targetNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetNode?.focus();
    },
    [fieldRefs],
  );

  const validateField = async (name, value) => {
    try {
      await validationRules.validateAt(name, { [name]: value });
      setErrors(prev => {
        const errors = { ...prev };
        delete errors[name];
        return errors;
      });
    } catch (error) {
      setErrors(prev => ({ ...prev, [name]: error.message }));
    }
  };

  const validateForm = async data => {
    try {
      await validationRules.validate(data, { abortEarly: false });
      setErrors({});

      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach(e => {
        // if there is already an error message for a field, keep it
        if (validationErrors.hasOwnProperty(e.path)) {
          return;
        }
        validationErrors[e.path] = e.message;
      });
      setErrors(validationErrors);

      const firstErrorField = Object.keys(validationErrors)[0];
      scrollAndFocusErrorField(firstErrorField);

      return false;
    }
  };

  // possibility to set error for a specific field without using yup
  const setError = (name, message) => {
    setErrors(prev => ({ ...prev, [name]: message }));
  };

  return {
    errors,
    fieldRefs,
    validateField,
    validateForm,
    scrollAndFocusErrorField,
    setError,
  };
};
